/**
 * CORE — deterministic state hashing for replay verification.
 *
 * `ReplayData.stateHash` must be a pure, platform-independent function of the
 * final run state. Two machines that re-simulate the same replay must produce
 * the SAME string, so the Worker can cheaply reject tampering and dedupe runs.
 *
 * Approach:
 *   - Quantize every float to a fixed integer grid before hashing. Two runs that
 *     are deterministic at the double-precision level are already bit-identical,
 *     but quantization gives a safety margin and makes the hash robust to JSON
 *     round-tripping of the metrics.
 *   - Fold the quantized integers through FNV-1a (same family the Architect uses
 *     for the daily seed), producing a stable 32-bit digest rendered as hex.
 *
 * Owned by: Core. Internal helper consumed by the Simulation + verifyReplay.
 */

import type { Vec3, Quat, ScoreMetrics } from '@/types';

/** Fixed-point scale for positions/velocities: 1e4 => 0.1mm grid. */
const POS_SCALE = 10000;
/** Fixed-point scale for unit quaternion components. */
const ROT_SCALE = 1000000;

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** A running FNV-1a accumulator over a stream of signed 32-bit integers. */
export class HashAccumulator {
  private state = FNV_OFFSET >>> 0;

  /** Fold one signed 32-bit integer (4 bytes, little-endian) into the digest. */
  pushInt(value: number): this {
    // Force a well-defined 32-bit integer regardless of input magnitude.
    let v = value | 0;
    for (let b = 0; b < 4; b++) {
      this.state ^= v & 0xff;
      this.state = Math.imul(this.state, FNV_PRIME) >>> 0;
      v >>= 8;
    }
    return this;
  }

  /** Quantize and fold a float (position/velocity scale). */
  pushFloat(value: number): this {
    return this.pushInt(quantize(value, POS_SCALE));
  }

  pushVec3(v: Vec3): this {
    return this.pushFloat(v[0]).pushFloat(v[1]).pushFloat(v[2]);
  }

  pushQuat(q: Quat): this {
    return this.pushInt(quantize(q[0], ROT_SCALE))
      .pushInt(quantize(q[1], ROT_SCALE))
      .pushInt(quantize(q[2], ROT_SCALE))
      .pushInt(quantize(q[3], ROT_SCALE));
  }

  /** Final 8-char lowercase hex digest. */
  digest(): string {
    return (this.state >>> 0).toString(16).padStart(8, '0');
  }
}

/**
 * Round to the nearest grid point deterministically. `Math.round` rounds half
 * toward +Infinity, which is consistent across all JS engines; we avoid the
 * sign-dependent behavior by rounding the absolute value and re-applying sign.
 */
function quantize(value: number, scale: number): number {
  if (!Number.isFinite(value)) return 0;
  const sign = value < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(value) * scale);
}

/**
 * Canonical replay hash: the final marble pose, velocity, and the integer
 * scoring outputs. Distance/time/checkpoints are folded so a tampered metric
 * changes the digest even if the pose somehow matches.
 */
export function hashFinalState(
  position: Vec3,
  rotation: Quat,
  velocity: Vec3,
  metrics: ScoreMetrics,
): string {
  const acc = new HashAccumulator();
  acc
    .pushVec3(position)
    .pushQuat(rotation)
    .pushVec3(velocity)
    .pushInt(metrics.finishTick)
    .pushInt(metrics.checkpointsReached)
    .pushFloat(metrics.distance)
    .pushInt(metrics.score);
  return acc.digest();
}
